import bbox from '@turf/bbox'

import MapFeature from './MapFeature'

/**
 * @typedef {Object} ViewportOptions
 * @property {import("maplibre-gl").PaddingOptions} [padding]
 */

/**
 * Control the map viewport and handle changes.
 * @fires Viewport#move
 * @fires Viewport#implicitmove
 * @fires Viewport#zoom
 * @fires Viewport#implicitzoom
 * @fires Viewport#boundschange
 */
export default class Viewport extends MapFeature {

    /** @type {import("maplibre-gl").LngLat} */
    _center

    /** @type {number} */
    _zoom

    /** @type {import("maplibre-gl").LngLatBounds} */
    _bounds

    /** @type {import("maplibre-gl").LngLatBounds} */
    _boundsToFit

    /** @type {import("maplibre-gl").PaddingOptions} */
    _padding

    /** @type {boolean} */
    _isAnimationEnabled = true

    /**
     * @param {ViewportOptions} [options]
     */
    constructor(options = {}) {
        super()

        this._padding = options.padding || 80

        this._handleCenterChange = this._handleCenterChange.bind(this)
        this._handleZoomChange = this._handleZoomChange.bind(this)
        this._handleResize = this._handleResize.bind(this)
    }

    /**
     * Make the map viewport contain a certain bounding box.
     * @param {import("maplibre-gl").LngLatBounds} bounds
     * @fires Viewport#implicitmove
     * @fires Viewport#implicitzoom
     * @fires Viewport#boundschange
     */
    fitBounds(bounds) {
        // Don't do everything for the same bounds again
        if (this._boundsDiffer(bounds, this._boundsToFit)) {
            this._boundsToFit = bounds

            if (this._map) {
                this._mapFitBounds(bounds)
            }
        }
    }

    /**
     * Fit to the bonding box around given geoJson.
     * @param {Object} geoJsom
     * @fires Viewport#implicitmove
     * @fires Viewport#implicitzoom
     * @fires Viewport#boundschange
     */
    fitGeoJsom(geoJsom) {
        this.fitBounds(bbox(geoJsom))
    }

    /**
     * Set the map viewport immediately instead of a fly-to animytion.
     * A complementary method setAnimation (with options) can be added when needed.
     */
    disableAnimation() {
        this._isAnimationEnabled = false
    }

    /** @type {import("maplibre-gl").LngLat} */
    get center() { return this._center }
    set center(center) {
        if (this._centerDiffers(center)) {
            this._center = center

            if (this._map) {
                this._map.setCenter(center)
            }
        }
    }

    /** @type {number} */
    get zoom() { return this._zoom }
    set zoom(zoom) {
        if (zoom !== this._zoom) {
            this._zoom = zoom

            if (this._map) {
                this._map.setZoom(zoom)
            }
        }
    }

    /** @param {import("maplibre-gl").Map} map */
    _onAdd(map) {
        if (this._center) {
            this._map.setCenter(this._center)
        } else {
            this._center = map.getCenter()
        }

        if (this._zoom) {
            this._map.setZoom(this._zoom)
        } else {
            this._zoom = map.getZoom()
        }

        if (this._boundsToFit) {
            this._mapFitBounds(this._boundsToFit)
        } else {
            this._handleBoundsChange()
        }

        map.on('moveend', this._handleCenterChange)
        map.on('zoomend', this._handleZoomChange)
        map.on('resize', this._handleResize)
    }

    /** @param {import("maplibre-gl").Map} map */
    _onRemove(map) {
        map.on('resize', this._handleResize)
        map.off('zoomend', this._handleZoomChange)
        map.off('moveend', this._handleCenterChange)
    }

    /**
     * @param {import("maplibre-gl").LngLatBounds} bounds
     */
    _mapFitBounds(bounds) {
        const options = {
            padding: this._padding,
        }

        if (!this._isAnimationEnabled) {
            options.duration = 0
        }

        this._map.fitBounds(bounds, options, {
            triggeredByFitBounds: true,
        })
    }

    /**
     * @param {maplibregl.MapboxEvent<MouseEvent|TouchEvent|WheelEvent> & maplibregl.EventData} e
     */
    _handleCenterChange(e) {
        const center = this._map.getCenter()

        if (this._centerDiffers(center)) {
            this._center = center

            if (e.triggeredByFitBounds) {
                /**
                 * @event Viewport#implicitmove Map has moved implicitly
                 * @type {Object}
                 * @property {import("maplibre-gl").LngLat} center
                 */
                this._dispatchEvent('implicitmove', { center })
            } else {
                /**
                 * @event Viewport#move Map has been actively moved
                 * @type {Object}
                 * @property {import("maplibre-gl").LngLat} center
                 */
                this._dispatchEvent('move', { center })
            }

            this._handleBoundsChange(e.triggeredByFitBounds)
        }
    }

    /**
     * @param {maplibregl.MapboxEvent<MouseEvent|TouchEvent|WheelEvent> & maplibregl.EventData} e
     */
    _handleZoomChange(e) {
        const zoom = this._map.getZoom()

        if (zoom !== this._zoom) {
            this._zoom = zoom

            if (e.triggeredByFitBounds) {
                /**
                 * @event Viewport#implicitzoom Map has zoomed implicitly
                 * @type {Object}
                 * @property {number} zoom
                 */
                this._dispatchEvent('implicitzoom', { zoom })
            } else {
                /**
                 * @event Viewport#zoom Map has been actively zoomed
                 * @type {Object}
                 * @property {number} zoom
                 */
                this._dispatchEvent('zoom', { zoom })
            }

            this._handleBoundsChange(e.triggeredByFitBounds)
        }
    }

    _handleResize() {
        this._handleBoundsChange()
    }

    /**
     * @param {boolean} triggeredByFitBounds
     */
    _handleBoundsChange(triggeredByFitBounds = false) {
        const bounds = this._map.getBounds()

        if (this._boundsDiffer(bounds)) {
            this._bounds = bounds
            if (!triggeredByFitBounds) {
                // Bounds changed after fitBounds, so fitting to the previous bounds makes sense again
                this._boundsToFit = null
            }

            /**
             * @event Viewport#boundschange Bounds changed in general
             * @type {Object}
             * @property {import("maplibre-gl").LngLatBounds} bounds
             */
            this._dispatchEvent('boundschange', { bounds })
        }
    }

    /**
     * @param {import("maplibre-gl").LngLat} center Value to compare to the current one
     */
    _centerDiffers(center) {
        return !this._center || center.toString() !== this._center.toString()
    }

    /**
     * @param {import("maplibre-gl").LngLatBounds} bounds Value to compare to the current one
     * @param {import("maplibre-gl").LngLatBounds} [currentBounds] Optional different current value to compare to
     */
    _boundsDiffer(bounds, currentBounds = this._bounds) {
        return !currentBounds || bounds.toString() !== currentBounds.toString()
    }

}
